/**
 * Hand simulator settings — shared keep rules for every probability tool.
 *
 * A single persisted source of truth so the sample hand and the batch run can
 * never disagree about what "keepable" means for the same deck.
 */
import { atomWithStorage } from 'jotai/utils';

import type { KeepRules, TurnSimOptions } from '@/features/decks/lib/handSim';
import type { DeckFormat } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface HandSimSettings {
  /** null = follow the format-derived default */
  overrides: KeepRules | null;
  /** Turn 1 skips the draw step when on the play */
  onThePlay: boolean;
  /** Turns the sample hand steps through */
  simTurns: number;
}

// ============================================================================
// Bounds & defaults
// ============================================================================

export const MIN_SOURCES_RANGE = { min: 0, max: 7 } as const;
export const MAX_LANDS_RANGE = { min: 0, max: 7 } as const;
export const SLOW_START_RANGE = { min: 1, max: 7 } as const;
export const SIM_TURNS_RANGE = { min: 3, max: 10 } as const;

export const DEFAULT_HAND_SIM_SETTINGS: HandSimSettings = {
  overrides: null,
  onThePlay: true,
  simTurns: 6,
};

/**
 * Format-derived keep rules. Commander decks want a third source and tolerate a
 * later first play; 60-card and limited formats want a play by turn 3.
 */
export function defaultKeepRules(format: DeckFormat | null): KeepRules {
  switch (format) {
    case 'commander':
    case 'brawl':
    case 'oathbreaker':
      return { minSources: 3, maxLands: 5, slowStartTurn: 4 };
    default:
      return { minSources: 2, maxLands: 5, slowStartTurn: 3 };
  }
}

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Resolve the active rules, clamping every field so a hand-edited localStorage
 * entry can't produce a nonsensical simulation.
 */
export function resolveKeepRules(settings: HandSimSettings, format: DeckFormat | null): KeepRules {
  const fallback = defaultKeepRules(format);
  if (!settings.overrides) return fallback;

  const minSources = clamp(settings.overrides.minSources, MIN_SOURCES_RANGE.min, MIN_SOURCES_RANGE.max, fallback.minSources);
  const maxLands = clamp(settings.overrides.maxLands, MAX_LANDS_RANGE.min, MAX_LANDS_RANGE.max, fallback.maxLands);

  return {
    minSources: Math.min(minSources, maxLands),
    maxLands,
    slowStartTurn: clamp(settings.overrides.slowStartTurn, SLOW_START_RANGE.min, SLOW_START_RANGE.max, fallback.slowStartTurn),
  };
}

export function isFormatDefault(rules: KeepRules, format: DeckFormat | null): boolean {
  const defaults = defaultKeepRules(format);
  return rules.minSources === defaults.minSources && rules.maxLands === defaults.maxLands && rules.slowStartTurn === defaults.slowStartTurn;
}

/** Turn-sim options, clamped so a stale or hand-edited entry can't hang the loop. */
export function resolveTurnOptions(settings: HandSimSettings): TurnSimOptions {
  return {
    onThePlay: settings.onThePlay ?? DEFAULT_HAND_SIM_SETTINGS.onThePlay,
    turns: clamp(settings.simTurns, SIM_TURNS_RANGE.min, SIM_TURNS_RANGE.max, DEFAULT_HAND_SIM_SETTINGS.simTurns),
  };
}

// ============================================================================
// Atoms
// ============================================================================

export const handSimSettingsAtom = atomWithStorage<HandSimSettings>('mtgcraft:hand-sim-settings', DEFAULT_HAND_SIM_SETTINGS);
